import { useState } from 'react';
import { Box, IconButton, Popover } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { HexColorPicker, HexColorInput } from 'react-colorful';

interface ColorPickerPopoverProps {
  /** Hex color without '#' prefix (e.g. 'ff0000') */
  color: string;
  /** Called with hex color without '#' prefix */
  onChange: (color: string) => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

/**
 * A color button that opens a HexColorPicker popover on click.
 * Includes a hex text input inside the popover for precise entry.
 */
export default function ColorPickerPopover({
  color,
  onChange,
  'data-testid': dataTestId,
  'aria-label': ariaLabel = 'Choose color',
}: ColorPickerPopoverProps) {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const hexWithHash = `#${color}`;

  const handleChange = (hex: string) => {
    onChange(hex.replace('#', ''));
  };

  return (
    <>
      <Box
        component="button"
        ref={setAnchorEl}
        data-testid={dataTestId}
        onClick={() => setOpen(true)}
        aria-label={ariaLabel}
        sx={{
          width: 36,
          height: 36,
          borderRadius: '4px',
          backgroundColor: hexWithHash,
          cursor: 'pointer',
          border: '2px solid #888',
          outline: 'none',
          p: 0,
          '&:hover': { border: '2px solid #333' },
          '&:focus-visible': {
            outline: '0.25rem solid #2491ff',
            outlineOffset: '0px',
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { overflow: 'visible' } } }}
      >
        <Box sx={{ p: 1.5, pt: 4.5, position: 'relative' }} data-testid="color-picker-popover">
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            aria-label="Close color picker"
            sx={{ position: 'absolute', top: 2, right: 2, zIndex: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <HexColorPicker color={hexWithHash} onChange={handleChange} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 1.5,
              gap: 0.5,
              fontSize: '0.875rem',
            }}
          >
            <span>#</span>
            <HexColorInput
              color={hexWithHash}
              onChange={handleChange}
              aria-label="Hex color value"
              data-testid="color-picker-hex-input"
              style={{
                width: '100%',
                padding: '4px 6px',
                border: '1px solid #000',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'inherit',
              }}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
}
